import React, { useState, useEffect, useRef } from 'react';

const Terminal = ({ setView }) => {
  const [history, setHistory] = useState([]);
  const historyIndexRef = useRef(-1);
  const [input, setInput] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [visitorIp, setVisitorIp] = useState('...');
  const bottomRef = useRef(null);

  const WELCOME = "Welcome! Type 'help' for available commands. Tab to autocomplete, ↑↓ for history.";
  const COMMANDS = ['help', 'about', 'skills', 'experience', 'projects', 'contact', 'resume', 'ls', 'cat', 'whoami', 'clear', 'exit'];

  // Fetch visitor IP
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(r => r.json())
      .then(d => setVisitorIp(d.ip || 'unknown'))
      .catch(() => setVisitorIp('unknown'));
  }, []);

  // Typing animation for welcome message
  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      if (i < WELCOME.length) {
        setDisplayText(WELCOME.substring(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
        setIsTyping(false);
      }
    }, 25);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const catFiles = {
    'about.txt': `Nomit Verma — SDE-III @ Certa.ai

Software Engineer with ~6 years of experience driving technological
innovation. Specializing in microservices architecture, API optimizations,
and cloud computing. Adept at leading cross-functional teams and
implementing Agile methodologies.`,
    'skills.json': `{
  "languages": ["Python", "JavaScript", "SQL", "HTML/CSS"],
  "frameworks": ["Django", "React", "React Native", "REST", "gRPC"],
  "devops": ["Git", "Docker", "Jenkins", "Kubernetes"],
  "databases": ["MySQL", "PostgreSQL", "MongoDB", "Firebase"],
  "cloud": ["AWS", "Kafka"]
}`,
    'contact.json': `{
  "email": "nomitverma1@gmail.com",
  "linkedin": "linkedin.com/in/nomit-verma",
  "github": "github.com/nomitv"
}`,
  };

  const handleCommand = (e) => {
    if (e.key === 'Enter') {
      const raw = input.trim();
      const cmd = raw.toLowerCase();
      let output = null;

      if (cmd === 'help') {
        output = (
          <div className="text-on-surface-variant mt-2 space-y-1 text-sm">
            <div><span className="text-on-surface font-bold w-28 inline-block">help</span> — Show available commands</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">about</span> — About me</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">skills</span> — Technical skills</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">experience</span> — Work experience</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">projects</span> — Personal projects</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">contact</span> — Contact information</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">resume</span> — Open resume PDF in new tab</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">ls</span> — List directory</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">cat [file]</span> — View file contents</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">whoami</span> — Current user</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">clear</span> — Clear the terminal</div>
            <div><span className="text-on-surface font-bold w-28 inline-block">exit</span> — Switch to GUI mode</div>
          </div>
        );
      } else if (cmd === 'clear') {
        setHistory([]);
        setInput('');
        historyIndexRef.current = -1;
        return;
      } else if (cmd.startsWith('cat ')) {
        const filename = cmd.slice(4).trim();
        if (catFiles[filename]) {
          output = (
            <pre className="mt-2 text-sm text-on-surface-variant bg-surface-container-low p-4 rounded border border-outline-variant/10 overflow-x-auto whitespace-pre-wrap">{catFiles[filename]}</pre>
          );
        } else if (filename === 'experience/' || filename === 'projects/') {
          output = <div className="text-error mt-2 text-sm">cat: {filename}: Is a directory. Try the '{filename.replace('/', '')}' command instead.</div>;
        } else {
          output = <div className="text-error mt-2 text-sm">cat: {filename}: No such file or directory</div>;
        }
      } else if (cmd === 'cat') {
        output = <div className="text-on-surface-variant mt-2 text-sm">Usage: cat [filename] — Try: cat about.txt, cat skills.json, cat contact.json</div>;
      } else if (cmd === 'about') {
        output = (
          <div className="bg-surface-container-low p-4 mt-2 text-on-surface-variant max-w-3xl leading-relaxed border-l-2 border-primary text-sm">
            <p className="mb-2"><span className="text-on-surface font-bold">Nomit Verma</span> — SDE-III @ Certa.ai</p>
            <p>~6 years of experience driving technological innovation. Specializing in microservices architecture, API optimizations, and cloud computing. Adept at leading cross-functional teams and implementing Agile methodologies.</p>
          </div>
        );
      } else if (cmd === 'skills') {
        output = (
          <div className="mt-2 space-y-3">
            {[
              { label: 'Languages & Frameworks', items: ['Python', 'Django', 'React', 'React Native', 'JavaScript', 'SQL', 'HTML/CSS'] },
              { label: 'DevOps & CI/CD', items: ['Git', 'Docker', 'Jenkins', 'Kubernetes'] },
              { label: 'Databases', items: ['MySQL', 'PostgreSQL', 'MongoDB', 'Firebase'] },
              { label: 'Other', items: ['AWS', 'Kafka', 'REST', 'gRPC'] },
            ].map(group => (
              <div key={group.label}>
                <span className="text-secondary text-xs font-mono">{group.label}:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {group.items.map(s => (
                    <span key={s} className="px-2.5 py-1 text-[11px] font-mono bg-surface-container-low border border-outline-variant/20 text-on-surface-variant rounded">{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      } else if (cmd === 'experience') {
        output = (
          <div className="mt-2 space-y-4 text-sm">
            <div className="border-l-2 border-primary pl-4">
              <div className="text-on-surface font-bold">Software Development Engineer - III</div>
              <div className="text-secondary text-xs font-mono mb-2">Certa.ai · Delhi, IN · Dec 2024 – Present</div>
              <ul className="text-on-surface-variant space-y-1">
                <li>→ Centralized comments microservice: 99.9% availability, {'<'}100ms latency</li>
                <li>→ Shared auth package adopted by 15+ microservices, 80% less duplicate code</li>
                <li>→ SSO redesign with transformational rules engine for flexible IDP mapping</li>
              </ul>
            </div>
            <div className="border-l-2 border-outline-variant pl-4">
              <div className="text-on-surface font-bold">Senior Software Engineer</div>
              <div className="text-secondary text-xs font-mono mb-2">Spinny · Gurgaon, IN · Aug 2021 – Dec 2024</div>
              <ul className="text-on-surface-variant space-y-1">
                <li>→ 60% infra cost reduction via Kubernetes migration</li>
                <li>→ Memory optimization: 95% → 10%</li>
                <li>→ 60% faster API response times</li>
                <li>→ Scheduler: 1,200 → 4,000 daily assignments</li>
                <li>→ Lead Developer for 2+ years</li>
              </ul>
            </div>
            <div className="border-l-2 border-outline-variant pl-4">
              <div className="text-on-surface font-bold">Full Stack Developer</div>
              <div className="text-secondary text-xs font-mono mb-2">McKinley & Rice · Noida, IN · Aug 2020 – Aug 2021</div>
              <ul className="text-on-surface-variant space-y-1">
                <li>→ Built Rexpert: resume enhancement platform</li>
                <li>→ Led EmergApp: Employee Management + SOS system</li>
              </ul>
            </div>
          </div>
        );
      } else if (cmd === 'projects') {
        output = (
          <div className="mt-2 space-y-3 text-sm">
            {[
              { name: 'Home NAS Server', desc: 'TrueNAS with RAID-Z2, 14TB+', status: 'ONLINE' },
              { name: 'Media Streaming', desc: 'Plex with hardware transcoding', status: 'ACTIVE' },
              { name: 'Portfolio Site', desc: 'Dual-mode GUI + Terminal (this!)', status: 'LIVE' },
              { name: 'Home Automation', desc: 'IoT hub with energy monitoring', status: 'RUNNING' },
            ].map(p => (
              <div key={p.name} className="border-l-2 border-outline-variant pl-4 flex items-center justify-between">
                <div>
                  <div className="text-on-surface font-bold">{p.name}</div>
                  <div className="text-on-surface-variant text-xs">{p.desc}</div>
                </div>
                <span className="text-[11px] font-mono px-2.5 py-0.5 bg-emerald-500/10 text-emerald-500 rounded ml-4 shrink-0">{p.status}</span>
              </div>
            ))}
          </div>
        );
      } else if (cmd === 'contact') {
        output = (
          <div className="font-mono text-sm mt-2 text-on-surface-variant">
            <pre className="bg-surface-container-low p-4 rounded border border-outline-variant/10 overflow-x-auto">
{`{
  "email": "nomitverma1@gmail.com",
  "linkedin": "linkedin.com/in/nomit-verma",
  "github": "github.com/nomitv"
}`}
            </pre>
          </div>
        );
      } else if (cmd === 'ls') {
        output = (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
            {['about.txt', 'skills.json', 'contact.json', 'experience/', 'projects/'].map(f => (
              <div key={f} className={`border border-outline-variant/20 p-2 flex items-center text-sm ${f.endsWith('/') ? 'text-primary' : 'text-on-surface-variant'}`}>
                <span className="material-symbols-outlined text-xs mr-2" style={{ fontVariationSettings: "'FILL' 1" }}>{f.endsWith('/') ? 'folder' : 'description'}</span>
                {f}
              </div>
            ))}
          </div>
        );
      } else if (cmd === 'whoami') {
        output = (
          <div className="mt-2 text-sm space-y-1 font-mono">
            <div><span className="text-on-surface-variant">user:</span> <span className="text-primary">visitor</span></div>
            <div><span className="text-on-surface-variant">host:</span> <span className="text-primary">nomitverma</span></div>
            <div><span className="text-on-surface-variant">ip:</span> <span className="text-primary">{visitorIp}</span></div>
            <div><span className="text-on-surface-variant">connected to:</span> <span className="text-primary">nomit@certa.ai (SDE-III)</span></div>
          </div>
        );
      } else if (cmd === 'resume') {
        window.open('/assets/nomit_verma_resume_online.pdf', '_blank', 'noopener,noreferrer');
        output = <div className="text-emerald-500 mt-2 text-sm font-mono">Opening resume in new tab...</div>;
      } else if (cmd === 'exit') {
        setView('gui');
        return;
      } else if (cmd !== '') {
        output = <div className="text-error mt-2 text-sm">bash: {raw}: command not found. Type 'help' for available commands.</div>;
      }

      if (cmd !== '') {
        setHistory(prev => [...prev, { command: raw, output }]);
      }
      setInput('');
      historyIndexRef.current = -1;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length > 0) {
        if (historyIndexRef.current < history.length - 1) historyIndexRef.current += 1;
        setInput(history[history.length - 1 - historyIndexRef.current].command);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndexRef.current > 0) {
        historyIndexRef.current -= 1;
        setInput(history[history.length - 1 - historyIndexRef.current].command);
      } else if (historyIndexRef.current === 0) {
        historyIndexRef.current = -1;
        setInput('');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const currentInput = input.toLowerCase();
      if (currentInput.length > 0) {
        // Handle "cat " prefix autocomplete for filenames
        if (currentInput.startsWith('cat ')) {
          const partial = currentInput.slice(4);
          const files = Object.keys(catFiles);
          const matches = files.filter(f => f.startsWith(partial));
          if (matches.length === 1) setInput(`cat ${matches[0]}`);
          else if (matches.length > 1) {
            setHistory(prev => [...prev, { command: input, output: <div className="text-on-surface-variant text-sm mt-1">Files: {matches.join('  ')}</div> }]);
          }
        } else {
          const matches = COMMANDS.filter(c => c.startsWith(currentInput));
          if (matches.length === 1) setInput(matches[0]);
          else if (matches.length > 1) {
            setHistory(prev => [...prev, { command: input, output: <div className="text-on-surface-variant text-sm mt-1">Matches: {matches.join('  ')}</div> }]);
          }
        }
      }
    }
  };

  return (
    <>
      <div className="crt-overlay fixed inset-0 pointer-events-none z-50"></div>

      <main className="h-full w-full overflow-hidden flex flex-col pt-4 md:pt-8 pb-12" onClick={() => document.getElementById('term-input')?.focus()}>
        <div className="max-w-5xl mx-auto w-full h-full flex flex-col px-4 md:px-8">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 md:mb-8 font-label text-xs uppercase tracking-[0.15em] border-b border-outline-variant/20 pb-4">
            <div className="flex items-center space-x-4 md:space-x-6">
              <div className="flex items-center space-x-2">
                <span className="text-on-surface-variant/50">HOST:</span>
                <span className="text-primary terminal-glow">NOMIT VERMA</span>
              </div>
              <div className="hidden md:flex items-center space-x-2">
                <span className="text-on-surface-variant/50">YOU:</span>
                <span className="text-primary">VISITOR</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
              <span className="text-on-surface-variant/50">CONNECTED</span>
            </div>
          </div>

          {/* Terminal */}
          <div className="flex-grow bg-surface-container-lowest border border-outline-variant/10 p-4 md:p-6 font-label overflow-y-auto relative rounded">
            <div className="space-y-1 mb-6">
              <p className="text-on-surface-variant/40 text-xs md:text-sm">Portfolio CLI v2.4.0</p>
              <p className="text-on-surface-variant/40 text-xs md:text-sm">Connecting... <span className="text-primary">[OK]</span></p>
            </div>

            <div className="mb-8 text-primary opacity-90 terminal-glow">
              <pre className="text-[8px] leading-tight md:text-xs font-mono overflow-x-auto">
{`███╗   ██╗ ██████╗ ███╗   ███╗██╗████████╗
████╗  ██║██╔═══██╗████╗ ████║██║╚══██╔══╝
██╔██╗ ██║██║   ██║██╔████╔██║██║   ██║   
██║╚██╗██║██║   ██║██║╚██╔╝██║██║   ██║   
██║ ╚████║╚██████╔╝██║ ╚═╝ ██║██║   ██║   
╚═╝  ╚═══╝ ╚═════╝ ╚═╝     ╚═╝╚═╝   ╚═╝`}
              </pre>
              <p className="mt-4 text-on-surface-variant text-xs md:text-sm">
                {displayText}
                {isTyping && <span className="animate-blink w-2 h-4 bg-primary inline-block align-middle ml-[1px]"></span>}
              </p>
            </div>

            <div className="space-y-4 md:space-y-6">
              {history.map((entry, idx) => (
                <div key={idx}>
                  <div className="flex items-center space-x-2 text-xs md:text-sm">
                  <span className="text-secondary shrink-0">visitor@nomitverma:~$</span>
                    <span className="text-primary">{entry.command}</span>
                  </div>
                  {entry.output}
                </div>
              ))}

              <div className="flex items-center space-x-2 pt-2">
                <span className="text-secondary text-xs md:text-sm whitespace-nowrap shrink-0">visitor@nomitverma:~$</span>
                <div className="relative flex items-center flex-1 min-w-0">
                  <span className="text-primary font-mono text-xs md:text-sm whitespace-pre">
                    {input}<span className="animate-blink w-2 h-4 bg-primary inline-block align-middle ml-[1px]"></span>
                  </span>
                  <input 
                    id="term-input"
                    autoFocus 
                    className="absolute inset-0 bg-transparent border-none outline-none ring-0 w-full focus:ring-0 z-10 font-mono text-xs md:text-sm pl-0 opacity-0 cursor-text" 
                    type="text" 
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleCommand}
                    autoComplete="off"
                    spellCheck="false"
                  />
                </div>
              </div>
              <div ref={bottomRef} className="h-4"></div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Terminal;
